import {
  LIVE_DEMO_SESSION_KEY,
  isLiveDemoSession,
} from '@gokrew/ui/utility/liveDemo';
import styles from './LiveDemoDriver.module.css';

/**
 * The company portal side of the CRM live AI demo. When the portal runs in
 * the demo iframe (signed in as the read-only demo viewer, `live_demo=1` on
 * the sign-in landing), the CRM page drives it by postMessage: open a path,
 * click or point at an element by its visible text, scroll, and read a text
 * summary of the screen that the voice agent talks from. Messages are only
 * accepted from a CRM host of the same domain as the portal, and nothing
 * here runs in a normal (top-level) portal tab.
 */

const CHANNEL = 'kh-live-demo';
const SETTLE_TIMEOUT_MS = 8000;
const LOADING_SELECTOR =
  '[data-slot="skeleton"], .animate-spin, [aria-busy="true"]';
const CLICKABLE_SELECTOR = [
  'button',
  'a[href]',
  '[role="tab"]',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="treeitem"]',
  '[role="checkbox"]',
  '[role="switch"]',
  'tr',
  'summary',
  'label',
].join(',');

type Command =
  | { cmd: 'hello' }
  | { cmd: 'navigate'; path: string }
  | { cmd: 'click'; target: string }
  | { cmd: 'point'; target: string }
  | { cmd: 'read' }
  | { cmd: 'scroll'; direction: 'up' | 'down' }
  | { cmd: 'back' };

interface Request {
  channel: typeof CHANNEL;
  id: string;
  command: Command;
}

let parentOrigin: string | null = null;
let cursor: HTMLDivElement | null = null;

/**
 * A CRM host sharing the portal's domain: crm.kollabhr.com for
 * app.kollabhr.com, staging-crm.kollabhr.com for staging-app.kollabhr.com.
 */
export function isAllowedParentOrigin(origin: string, portalHost: string) {
  try {
    const url = new URL(origin);
    const parentLabels = url.hostname.split('.');
    const portalLabels = portalHost.split(':')[0].split('.');
    if (parentLabels.length < 2 || !/^(crm|.+-crm)$/.test(parentLabels[0]))
      return false;
    const domain = (labels: string[]) => labels.slice(-2).join('.');
    if (domain(parentLabels) !== domain(portalLabels)) return false;
    const local = ['localhost', 'localtest.me'].includes(domain(portalLabels));
    return url.protocol === 'https:' || local;
  } catch {
    return false;
  }
}

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

function isVisible(element: Element) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.closest('[aria-hidden="true"], [hidden], [inert]')) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

const label = (element: Element) =>
  (
    element.getAttribute('aria-label') ||
    (element as HTMLElement).innerText ||
    element.textContent ||
    ''
  )
    .replace(/\s+/g, ' ')
    .trim();

/** The open dialog if there is one: the AI acts inside it first. */
function scope(): ParentNode {
  const dialogs = [
    ...document.querySelectorAll('[role="dialog"], [role="alertdialog"]'),
  ].filter(isVisible);
  return dialogs[dialogs.length - 1] ?? document;
}

function findTarget(target: string): HTMLElement | null {
  const wanted = normalize(target);
  if (!wanted) return null;
  let best: { element: HTMLElement; score: number; length: number } | null =
    null;
  for (const element of scope().querySelectorAll(CLICKABLE_SELECTOR)) {
    if (!isVisible(element)) continue;
    const text = normalize(label(element));
    if (!text) continue;
    let score = 0;
    if (text === wanted) score = 4;
    else if (text.startsWith(wanted)) score = 3;
    else if (text.includes(wanted)) score = 2;
    else if (wanted.includes(text) && text.length > 3) score = 1;
    if (!score) continue;
    // The most specific match wins: a cell's link over its whole row.
    const length = text.length;
    if (
      !best ||
      score > best.score ||
      (score === best.score && length < best.length)
    )
      best = { element: element as HTMLElement, score, length };
  }
  return best?.element ?? null;
}

function ensureCursor() {
  if (cursor) return cursor;
  cursor = document.createElement('div');
  cursor.className = styles.cursor;
  cursor.setAttribute('aria-hidden', 'true');
  cursor.innerHTML = `<svg viewBox="0 0 24 24"><path class="${
    styles.cursorFill
  }" d="M4 2.5 19.5 12l-6.9 1.6L9.4 20 4 2.5Z"/></svg>`;
  cursor.style.transform = `translate(${window.innerWidth / 2}px, ${window.innerHeight / 2}px)`;
  document.body.appendChild(cursor);
  return cursor;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function moveTo(element: HTMLElement) {
  element.scrollIntoView({ block: 'center', inline: 'nearest' });
  await wait(150);
  const rect = element.getBoundingClientRect();
  const pointer = ensureCursor();
  pointer.style.transform = `translate(${rect.left + Math.min(rect.width / 2, 40)}px, ${rect.top + rect.height / 2}px)`;
  await wait(480);
  return rect;
}

function highlight(rect: DOMRect, ms: number) {
  const box = document.createElement('div');
  box.className = styles.highlight;
  box.setAttribute('aria-hidden', 'true');
  const pad = 4;
  Object.assign(box.style, {
    left: `${rect.left - pad}px`,
    top: `${rect.top - pad}px`,
    width: `${rect.width + pad * 2}px`,
    height: `${rect.height + pad * 2}px`,
  });
  document.body.appendChild(box);
  setTimeout(() => box.remove(), ms);
}

/** Radix tabs and menus open on pointerdown, React buttons on click: send both. */
function press(element: HTMLElement, rect: DOMRect) {
  const clientX = rect.left + Math.min(rect.width / 2, 40);
  const clientY = rect.top + rect.height / 2;
  const init = {
    bubbles: true,
    cancelable: true,
    composed: true,
    clientX,
    clientY,
    button: 0,
    view: window,
  };
  element.dispatchEvent(
    new PointerEvent('pointerdown', { ...init, pointerType: 'mouse' })
  );
  element.dispatchEvent(new MouseEvent('mousedown', init));
  element.focus?.({ preventScroll: true });
  element.dispatchEvent(
    new PointerEvent('pointerup', { ...init, pointerType: 'mouse' })
  );
  element.dispatchEvent(new MouseEvent('mouseup', init));
  element.click();
  const pointer = ensureCursor();
  pointer.classList.remove(styles.pressed);
  void pointer.offsetWidth;
  pointer.classList.add(styles.pressed);
}

/** Waits for the page's loaders (skeletons, spinners) like the smoke walk. */
async function settle() {
  const started = Date.now();
  await wait(250);
  while (Date.now() - started < SETTLE_TIMEOUT_MS) {
    const loading = [...document.querySelectorAll(LOADING_SELECTOR)].some(
      isVisible
    );
    if (!loading) break;
    await wait(150);
  }
  await wait(300);
}

const unique = (items: string[], max: number) =>
  [...new Set(items.filter(Boolean))].slice(0, max);

const texts = (root: ParentNode, selector: string, max: number, cut = 80) =>
  unique(
    [...root.querySelectorAll(selector)]
      .filter(isVisible)
      .filter((element) => !element.closest('nav, aside'))
      .map((element) => label(element).slice(0, cut)),
    max
  );

/** What the agent reads: a compact text picture of the current screen. */
export function summarizeScreen(): string {
  const root = scope();
  const main =
    root === document
      ? (document.querySelector('main') ?? document.body)
      : (root as HTMLElement);
  const lines = [`Screen: ${document.title} (${window.location.pathname})`];
  if (root !== document) lines.push('A dialog is open.');
  const headings = texts(main, 'h1, h2, h3, [role="heading"]', 10);
  if (headings.length) lines.push(`Headings: ${headings.join(' / ')}`);
  const tabs = unique(
    [...main.querySelectorAll('[role="tab"]')]
      .filter(isVisible)
      .map(
        (tab) =>
          `${label(tab)}${tab.getAttribute('aria-selected') === 'true' ? ' (selected)' : ''}`
      ),
    15
  );
  if (tabs.length) lines.push(`Tabs: ${tabs.join(', ')}`);
  const buttons = texts(main, 'button, [role="button"]', 20, 50);
  if (buttons.length) lines.push(`Buttons: ${buttons.join(', ')}`);
  const table = [...main.querySelectorAll('table')].find(isVisible);
  if (table) {
    const header = unique(
      [...table.querySelectorAll('th')].map((cell) => label(cell)),
      12
    );
    if (header.length) lines.push(`Table columns: ${header.join(' | ')}`);
    const rows = [...table.querySelectorAll('tbody tr')]
      .filter(isVisible)
      .slice(0, 8)
      .map((row) =>
        [...row.querySelectorAll('td')]
          .map((cell) => label(cell).slice(0, 40))
          .filter(Boolean)
          .join(' | ')
      )
      .filter(Boolean);
    const total = table.querySelectorAll('tbody tr').length;
    if (rows.length)
      lines.push(
        `Rows (${total} shown on the page):`,
        ...rows.map((r) => `- ${r}`)
      );
  }
  const text = (main.innerText || main.textContent || '')
    .replace(/\s+/g, ' ')
    .trim();
  lines.push(`Visible text: ${text.slice(0, 900)}`);
  return lines.join('\n').slice(0, 2800);
}

function navigate(path: string) {
  if (!path.startsWith('/') || path.startsWith('//')) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
}

function scrollContainer(): HTMLElement {
  const candidates = [...document.querySelectorAll<HTMLElement>('main, main *')]
    .filter((element) => {
      const style = getComputedStyle(element);
      return (
        /(auto|scroll)/.test(style.overflowY) &&
        element.scrollHeight > element.clientHeight + 20
      );
    })
    .sort((a, b) => b.clientHeight - a.clientHeight);
  return candidates[0] ?? document.scrollingElement ?? document.body;
}

async function run(command: Command): Promise<string> {
  switch (command.cmd) {
    case 'hello':
      return window.location.pathname.startsWith('/login')
        ? 'signing-in'
        : 'ready';
    case 'navigate':
      navigate(command.path);
      await settle();
      return summarizeScreen();
    case 'click': {
      const element = findTarget(command.target);
      if (!element)
        return `Not found on this screen: "${command.target}".\n${summarizeScreen()}`;
      const rect = await moveTo(element);
      highlight(rect, 700);
      press(element, rect);
      await settle();
      return summarizeScreen();
    }
    case 'point': {
      const element = findTarget(command.target);
      if (!element) return `Not found on this screen: "${command.target}".`;
      highlight(await moveTo(element), 2600);
      return `Pointing at "${label(element).slice(0, 80)}".`;
    }
    case 'read':
      return summarizeScreen();
    case 'scroll': {
      const container = scrollContainer();
      const by = Math.round(container.clientHeight * 0.7);
      container.scrollBy({
        top: command.direction === 'up' ? -by : by,
        behavior: 'smooth',
      });
      await wait(600);
      return summarizeScreen();
    }
    case 'back':
      window.history.back();
      await settle();
      return summarizeScreen();
  }
}

function notifyNavigation() {
  if (!parentOrigin) return;
  window.parent.postMessage(
    {
      channel: CHANNEL,
      event: 'navigated',
      path: window.location.pathname,
      title: document.title,
    },
    parentOrigin
  );
}

/** Called once from main.tsx, before the app renders. */
export function installLiveDemoDriver() {
  const params = new URLSearchParams(window.location.search);
  try {
    if (params.get('live_demo') === '1' && window.self !== window.top)
      sessionStorage.setItem(LIVE_DEMO_SESSION_KEY, '1');
  } catch {
    return;
  }
  if (!isLiveDemoSession()) return;

  window.addEventListener('message', (event: MessageEvent<Request>) => {
    const data = event.data;
    if (
      event.source !== window.parent ||
      data?.channel !== CHANNEL ||
      typeof data.id !== 'string' ||
      !isAllowedParentOrigin(event.origin, window.location.host)
    )
      return;
    parentOrigin = event.origin;
    void run(data.command)
      .catch((error: Error) => `The action failed: ${error.message}`)
      .then((result) =>
        window.parent.postMessage(
          { channel: CHANNEL, id: data.id, result },
          event.origin
        )
      );
  });

  // Tell the CRM page when the prospect clicks around on their own.
  let lastPath = window.location.pathname;
  setInterval(() => {
    if (window.location.pathname === lastPath) return;
    lastPath = window.location.pathname;
    setTimeout(notifyNavigation, 600);
  }, 400);
}
